import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * useCapabilities — ações disponíveis NESTE contexto, decididas pelo backend.
 *
 *   const caps = useCapabilities(["areas.create", "areas.edit"], { clientId, unitId });
 *   caps.can("areas.create")   -> boolean (false enquanto carrega ou se negado)
 *   caps.get("areas.create")   -> { allowed, blocked_by, message, ... } | null
 *
 * usePermissions().can(módulo, ação) responde pelo usuário em geral; aqui a
 * pergunta inclui empresa, unidade, área ou subárea. A resposta vem de
 * GET /api/permissions/me/capabilities — a MESMA decisão que a rota de
 * escrita aplica (backend/capabilities.py): disponibilidade do módulo para a
 * empresa, permissão efetiva e escopo do alvo. Nada de regra no frontend nem
 * de armazenamento local.
 *
 * Recalcula ao montar (entrar no módulo), quando o contexto muda (a resposta
 * de um contexto anterior nunca vale para o atual) e quando a autorização
 * efetiva do usuário muda: o AuthContext recarrega /permissions/me ao
 * detectar outra authz_version (lib/authzVersion.js) e o hook segue a versão.
 */
export function useCapabilities(checks, context = {}, { enabled = true } = {}) {
  const { user, authorization } = useAuth();
  const version = authorization?.authz_version || null;
  const checksKey = [...new Set((checks || []).filter(Boolean))].sort().join(",");
  const { clientId = null, unitId = null, areaId = null, subareaId = null } = context || {};
  const contextKey = [clientId, unitId, areaId, subareaId].map((v) => v || "").join("|");
  const active = Boolean(enabled && user?.id && checksKey);

  const [state, setState] = useState({ key: null, capabilities: {}, error: null });
  const sequence = useRef(0);
  const requestKey = `${user?.id || ""}#${checksKey}#${contextKey}`;

  const load = useCallback(async () => {
    if (!active) return;
    const current = ++sequence.current;
    try {
      const { data } = await api.get("/permissions/me/capabilities", {
        params: {
          checks: checksKey,
          client_id: clientId || undefined,
          unit_id: unitId || undefined,
          area_id: areaId || undefined,
          subarea_id: subareaId || undefined,
        },
      });
      if (current !== sequence.current) return;
      setState({ key: requestKey, capabilities: data?.capabilities || {}, error: null });
    } catch (error) {
      if (current !== sequence.current) return;
      setState({ key: requestKey, capabilities: {}, error });
    }
  }, [active, checksKey, clientId, unitId, areaId, subareaId, requestKey]);

  useEffect(() => {
    if (!active) {
      sequence.current += 1;
      setState({ key: null, capabilities: {}, error: null });
      return;
    }
    void load();
  }, [active, load, version]);

  // Só vale a resposta do contexto atual (troca de empresa/unidade/área).
  const current = state.key === requestKey ? state.capabilities : null;

  const get = useCallback((key) => (current && current[key]) || null, [current]);
  const can = useCallback((key) => Boolean(current && current[key]?.allowed), [current]);

  return useMemo(
    () => ({
      can,
      get,
      loading: active && current === null,
      error: state.key === requestKey ? state.error : null,
      refresh: load,
    }),
    [can, get, active, current, state.key, state.error, requestKey, load],
  );
}

export default useCapabilities;
